import { useEffect, useState } from "react";
import { toast } from "sonner";
import { partnerAdminApi, bpsToPct, centsToUsd } from "@/lib/partner";

const STATUSES = ["all", "pending", "active", "suspended", "revoked"];
const PARTNER_TYPES = ["all", "individual", "creator", "agency"];

export default function PartnersAdmin() {
  const [status, setStatus] = useState("all");
  const [partnerType, setPartnerType] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overrideOpen, setOverrideOpen] = useState(null); // partner_id
  const [detailId, setDetailId] = useState(null); // partner_id for drawer

  const load = async () => {
    setLoading(true);
    const params = { limit: 100, offset: 0 };
    if (status !== "all") params.status = status;
    if (partnerType !== "all") params.partner_type = partnerType;
    if (q.trim()) params.q = q.trim();
    try {
      const d = await partnerAdminApi.list(params);
      setItems(d.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [status, partnerType]);

  const doApprove = async (id) => {
    try { await partnerAdminApi.approve(id); toast.success("Partner approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doSuspend = async (id) => {
    if (!window.confirm("Suspend this partner? Attribution + commissions stop until reactivated.")) return;
    try { await partnerAdminApi.suspend(id); toast.success("Partner suspended"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const applyOverride = async (id, val) => {
    const rate_bps = val === "" ? null : Number(val);
    if (val !== "" && (isNaN(rate_bps) || rate_bps < 0 || rate_bps > 10000)) {
      return toast.error("Rate must be 0..10000 basis points (0..100%)");
    }
    try {
      await partnerAdminApi.tierOverride(id, rate_bps);
      toast.success(rate_bps == null ? "Override cleared" : `Override set to ${bpsToPct(rate_bps)}`);
      setOverrideOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-partners">
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search by code or user_id"
          className="w-64 rounded-lg border border-white/15 bg-white/[0.03] px-3 py-1.5 text-sm text-white outline-none placeholder:text-white/30 focus:border-[#D6A653]/50"
          data-testid="admin-partners-search"
        />
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`admin-filter-${s}`}
            className={`rounded-full border px-3 py-1.5 text-xs capitalize transition-colors ${
              status === s ? "border-[#D6A653] bg-[#D6A653]/12 text-[#D6A653]" : "border-white/12 text-white/55 hover:text-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="admin-partner-type-filters">
        <span className="text-[11px] uppercase tracking-wider text-white/40">Type</span>
        {PARTNER_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setPartnerType(t)}
            data-testid={`admin-type-filter-${t}`}
            className={`rounded-full border px-3 py-1.5 text-xs capitalize transition-colors ${
              partnerType === t ? "border-[#D6A653] bg-[#D6A653]/12 text-[#D6A653]" : "border-white/12 text-white/55 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {loading ? <p className="text-white/50">Loading…</p> : items.length === 0 ? <p className="text-white/40">No partners.</p> : (
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0B0D12]">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-white/40">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Tier</th>
                <th className="px-4 py-3 text-right">Earned</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3">Payout ready</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="text-white/80">
              {items.map((p) => (
                <tr key={p.id} className="border-t border-white/5" data-testid={`admin-partner-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-[#D6A653]">{p.partner_code}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm">{p.user_name || <span className="text-white/40">—</span>}</div>
                    <div className="text-[11px] text-white/45">{p.user_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {p.partner_type ? (
                      <span className="rounded-full border border-white/12 bg-white/[0.03] px-2 py-0.5 text-[11px] capitalize text-white/70" data-testid={`admin-partner-type-${p.id}`}>
                        {p.partner_type}
                      </span>
                    ) : <span className="text-white/30">—</span>}
                  </td>
                  <td className="px-4 py-3 capitalize">{p.status}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {p.tier_override != null ? <span className="text-[#D6A653]">{bpsToPct(p.tier_override)} (override)</span> : bpsToPct(p.tier_cached?.rate_bps || 1500)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{centsToUsd(p.total_earned_cents)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{centsToUsd(p.total_paid_cents)}</td>
                  <td className="px-4 py-3">{p.payout_ready ? <span className="text-emerald-300">yes</span> : <span className="text-white/40">no</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {p.status !== "active" ? (
                        <button data-testid={`approve-${p.id}`} onClick={() => doApprove(p.id)} className="rounded-lg border border-emerald-500/40 px-2.5 py-1 text-xs text-emerald-300 hover:bg-emerald-500/10">Approve</button>
                      ) : (
                        <button data-testid={`suspend-${p.id}`} onClick={() => doSuspend(p.id)} className="rounded-lg border border-red-500/40 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/10">Suspend</button>
                      )}
                      <button data-testid={`override-${p.id}`} onClick={() => setOverrideOpen(overrideOpen === p.id ? null : p.id)} className="rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5">Override</button>
                    </div>
                    {overrideOpen === p.id && (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="number"
                          placeholder="basis points (e.g. 3000 = 30%)"
                          defaultValue={p.tier_override ?? ""}
                          onKeyDown={(e) => e.key === "Enter" && applyOverride(p.id, e.currentTarget.value)}
                          data-testid={`override-input-${p.id}`}
                          className="w-52 rounded border border-white/15 bg-black/40 px-2 py-1 text-xs text-white"
                        />
                        <button data-testid={`override-clear-${p.id}`} onClick={() => applyOverride(p.id, "")} className="rounded border border-white/15 px-2 py-1 text-[11px] text-white/60">Clear</button>
                      </div>
                    )}
                    <button
                      onClick={() => setDetailId(p.id)}
                      data-testid={`view-${p.id}`}
                      className="mt-2 block text-[11px] text-white/50 underline underline-offset-2 hover:text-white"
                    >
                      View details →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailId ? (
        <PartnerDetailDrawer
          partnerId={detailId}
          onClose={() => setDetailId(null)}
          onMutated={() => { load(); }}
        />
      ) : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Partner detail drawer — the pro operator surface. Rendered as a right-side
// slide-in panel (no shadcn drawer dependency; plain fixed overlay).
// ---------------------------------------------------------------------------

const Row = ({ label, value, mono, copyable }) => {
  const copy = () => {
    if (!value) return;
    try { navigator.clipboard.writeText(String(value)); toast.success("Copied"); }
    catch { /* ignore */ }
  };
  return (
    <div className="flex items-start justify-between gap-3 border-t border-white/5 py-2 first:border-t-0 first:pt-0">
      <span className="text-[11px] uppercase tracking-wider text-white/40">{label}</span>
      <div className="flex max-w-[65%] items-center gap-2">
        <span className={`truncate text-right text-sm text-white/85 ${mono ? "font-mono" : ""}`}>
          {value || <span className="text-white/30">—</span>}
        </span>
        {copyable && value ? (
          <button onClick={copy} className="text-white/40 hover:text-white" title="Copy">📋</button>
        ) : null}
      </div>
    </div>
  );
};

function PartnerDetailDrawer({ partnerId, onClose, onMutated }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [reasonPrompt, setReasonPrompt] = useState(null); // {action, title}
  const [reason, setReason] = useState("");

  const load = () => {
    setLoading(true);
    partnerAdminApi.detail(partnerId).then(setD).finally(() => setLoading(false));
  };
  useEffect(load, [partnerId]);

  const runAction = async (fn, label) => {
    setBusy(label);
    try { await fn(); toast.success(`${label} — done`); load(); onMutated?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    finally { setBusy(""); }
  };

  const submitReason = async () => {
    if (!reasonPrompt) return;
    const r = reason.trim();
    if (!r) return toast.error("Please enter a reason.");
    const id = partnerId;
    const map = {
      reject: () => partnerAdminApi.reject(id, r),
      suspend: () => partnerAdminApi.suspend(id, r),
      revoke: () => partnerAdminApi.revoke(id, r),
    };
    await runAction(map[reasonPrompt.action], reasonPrompt.title);
    setReasonPrompt(null); setReason("");
  };

  const addNote = async () => {
    const body = noteBody.trim();
    if (!body) return;
    await runAction(() => partnerAdminApi.addNote(partnerId, body), "Add note");
    setNoteBody("");
  };

  const p = d?.partner || {};
  const u = d?.user || {};
  const isCreator = p.partner_type === "creator";
  const isAgency = p.partner_type === "agency";
  const isIndividual = p.partner_type === "individual";
  const rate = p.tier_override != null ? p.tier_override : (p.tier_cached?.rate_bps || 1500);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-partner-drawer">
      <button
        onClick={onClose}
        aria-label="Close"
        className="flex-1 bg-black/60"
        data-testid="admin-drawer-backdrop"
      />
      <div className="ml-auto flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l border-white/10 bg-[#0B0D12] p-6 text-white">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-medium">Partner detail</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="admin-drawer-close">Close</button>
        </div>

        {loading || !d ? (
          <p className="mt-8 text-white/50">Loading…</p>
        ) : (
          <>
            {/* Identity & profile */}
            <section className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="drawer-identity">
              <p className="text-[11px] uppercase tracking-wider text-white/40">Identity & application</p>
              <div className="mt-3 space-y-0">
                <Row label="User email" value={u.email} copyable />
                <Row label="User name" value={u.name || p.full_name} />
                <Row label="User id" value={u.id} mono copyable />
                <Row label="is_partner" value={String(!!u.is_partner || true)} />
                <Row label="Partner code" value={p.partner_code} mono copyable />
                <Row label="Partner type" value={p.partner_type} />
                <Row label="Status" value={p.status} />
                <Row label="Applied at" value={p.applied_at} />
                <Row label="Approved at" value={p.approved_at} />
                <Row label="Approved by" value={p.approved_by} mono />
                <Row label="Suspended at" value={p.suspended_at} />
                <Row label="Suspended reason" value={p.suspended_reason} />
                <Row label="Rejected at" value={p.rejected_at} />
                <Row label="Rejection reason" value={p.rejection_reason} />
                <Row label="Revoked at" value={p.revoked_at} />
                <Row label="Revoked reason" value={p.revoked_reason} />
                <Row label="Application source" value={p.application_source} />
                <Row label="TOS version" value={p.tos_version} mono />
                <Row label="Agreed terms at" value={p.agreed_terms_at || p.tos_accepted_at} />
                <Row label="Country" value={p.country} />
                <Row label="Payout email" value={p.payout_email} copyable />
              </div>
            </section>

            {/* Type-specific */}
            {(isIndividual || isCreator || isAgency) ? (
              <section className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="drawer-type-specific">
                <p className="text-[11px] uppercase tracking-wider text-white/40">
                  {p.partner_type} profile
                </p>
                <div className="mt-3">
                  {isIndividual && <Row label="Promo channels" value={(p.promo_channels || []).join(", ")} />}
                  {isCreator && (
                    <>
                      <Row label="Audience size" value={p.audience_size} />
                      <Row label="Primary platforms" value={(p.primary_platforms || []).join(", ")} />
                      <Row label="Legacy primary_platform" value={p.primary_platform} />
                      <Row label="Content niche" value={p.content_niche} />
                      <Row label="Website / handle" value={p.website_or_handle} />
                    </>
                  )}
                  {isAgency && (
                    <>
                      <Row label="Company name" value={p.company_name} />
                      <Row label="Company website" value={p.company_website} />
                      <Row label="Team size" value={p.team_size} />
                      <Row label="Client count est." value={p.client_count_estimate} />
                      <Row label="Services offered" value={p.services_offered} />
                    </>
                  )}
                </div>
              </section>
            ) : null}

            {/* Performance */}
            <section className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="drawer-performance">
              <p className="text-[11px] uppercase tracking-wider text-white/40">Performance</p>
              <div className="mt-3">
                <Row label="Current rate" value={`${bpsToPct(rate)}${p.tier_override != null ? " (override)" : ""}`} />
                <Row label="Active workspaces" value={d.counts?.active_workspaces} />
                <Row label="Workspaces total" value={d.counts?.workspaces} />
                <Row label="Commissions" value={d.counts?.commissions} />
                <Row label="Total earned" value={centsToUsd(p.total_earned_cents)} />
                <Row label="Total paid" value={centsToUsd(p.total_paid_cents)} />
                <Row label="Total pending" value={centsToUsd(p.total_pending_cents)} />
                <Row label="Payout ready" value={p.payout_ready ? "yes" : "no"} />
              </div>
            </section>

            {/* Admin actions */}
            <section className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="drawer-actions">
              <p className="text-[11px] uppercase tracking-wider text-white/40">Admin actions</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {p.status === "pending" && (
                  <button data-testid="drawer-approve" disabled={!!busy}
                    onClick={() => runAction(() => partnerAdminApi.approve(partnerId), "Approve")}
                    className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">Approve</button>
                )}
                {p.status === "pending" && (
                  <button data-testid="drawer-reject" disabled={!!busy}
                    onClick={() => setReasonPrompt({ action: "reject", title: "Reject application" })}
                    className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/10 disabled:opacity-50">Reject</button>
                )}
                {p.status === "active" && (
                  <button data-testid="drawer-suspend" disabled={!!busy}
                    onClick={() => setReasonPrompt({ action: "suspend", title: "Suspend partner" })}
                    className="rounded-lg border border-amber-500/40 px-3 py-1.5 text-xs text-amber-300 hover:bg-amber-500/10 disabled:opacity-50">Suspend</button>
                )}
                {p.status === "suspended" && (
                  <button data-testid="drawer-reactivate" disabled={!!busy}
                    onClick={() => runAction(() => partnerAdminApi.reactivate(partnerId), "Reactivate")}
                    className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">Reactivate</button>
                )}
                {p.status !== "revoked" && (
                  <button data-testid="drawer-revoke" disabled={!!busy}
                    onClick={() => setReasonPrompt({ action: "revoke", title: "Revoke partner (irreversible-ish)" })}
                    className="rounded-lg border border-red-600/40 px-3 py-1.5 text-xs text-red-400 hover:bg-red-600/10 disabled:opacity-50">Revoke</button>
                )}
              </div>

              {reasonPrompt ? (
                <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3" data-testid="drawer-reason-prompt">
                  <p className="text-xs text-white/70">{reasonPrompt.title}</p>
                  <textarea
                    value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (required)"
                    data-testid="drawer-reason-input"
                    className="mt-2 w-full rounded border border-white/15 bg-black/40 px-2 py-1 text-xs text-white"
                    rows={3}
                  />
                  <div className="mt-2 flex gap-2">
                    <button data-testid="drawer-reason-submit" onClick={submitReason} className="rounded bg-[#D6A653] px-3 py-1 text-xs font-medium text-black">Confirm</button>
                    <button onClick={() => { setReasonPrompt(null); setReason(""); }} className="rounded border border-white/15 px-3 py-1 text-xs text-white/60">Cancel</button>
                  </div>
                </div>
              ) : null}

              <div className="mt-4 border-t border-white/5 pt-4">
                <p className="text-[11px] uppercase tracking-wider text-white/40">Tier override</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input type="number" placeholder="basis points (e.g. 3000 = 30%)"
                    defaultValue={p.tier_override ?? ""}
                    onKeyDown={(e) => e.key === "Enter" && runAction(
                      () => partnerAdminApi.tierOverride(partnerId, e.currentTarget.value === "" ? null : Number(e.currentTarget.value)),
                      "Tier override")}
                    data-testid="drawer-tier-input"
                    className="w-60 rounded border border-white/15 bg-black/40 px-2 py-1 text-xs text-white" />
                  <button data-testid="drawer-tier-clear"
                    onClick={() => runAction(() => partnerAdminApi.tierOverride(partnerId, null), "Clear override")}
                    className="rounded border border-white/15 px-3 py-1 text-xs text-white/60">Clear</button>
                  <span className="text-[11px] text-white/40">Press Enter to save.</span>
                </div>
              </div>

              <div className="mt-4 border-t border-white/5 pt-4" data-testid="drawer-notes">
                <p className="text-[11px] uppercase tracking-wider text-white/40">Internal notes ({(p.admin_notes || []).length})</p>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {(p.admin_notes || []).map((n, i) => (
                    <div key={i} className="rounded border border-white/8 bg-black/30 p-2 text-xs">
                      <p className="text-white/70">{n.body}</p>
                      <p className="mt-1 text-[10px] text-white/40">{n.author_email} · {n.created_at?.slice(0, 16)}</p>
                    </div>
                  ))}
                  {(p.admin_notes || []).length === 0 && <p className="text-[11px] text-white/40">No notes yet.</p>}
                </div>
                <div className="mt-2 flex gap-2">
                  <input value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
                    placeholder="Add internal note (visible to admins only)"
                    data-testid="drawer-note-input"
                    onKeyDown={(e) => e.key === "Enter" && addNote()}
                    className="flex-1 rounded border border-white/15 bg-black/40 px-2 py-1 text-xs text-white" />
                  <button data-testid="drawer-note-submit" onClick={addNote} className="rounded bg-[#D6A653] px-3 py-1 text-xs font-medium text-black">Add</button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
